import { Link } from '@inertiajs/react';

import Badge from '@/components/public/ui/Badge';
import Icon from '@/components/public/ui/Icon';
import { trackUmamiEvent } from '@/lib/analytics';

export interface NewsItem {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string | null;
  date: string | null;
  imageUrl: string | null;
}

// Uniform surface cards; the category lives in the Badge + the (image-less)
// fallback tint. Icons are whitelisted Material Symbols.
const categoryConfig: Record<
  string,
  { badge: 'primary' | 'secondary' | 'tertiary'; icon: string; tintBg: string; tintIcon: string }
> = {
  Avisos: {
    badge: 'secondary',
    icon: 'campaign',
    tintBg: 'bg-secondary/10',
    tintIcon: 'text-secondary/45',
  },
  Eventos: { badge: 'primary', icon: 'event', tintBg: 'bg-primary/8', tintIcon: 'text-primary/35' },
  Obras: {
    badge: 'secondary',
    icon: 'build',
    tintBg: 'bg-secondary/10',
    tintIcon: 'text-secondary/45',
  },
  Assembleia: {
    badge: 'primary',
    icon: 'account_balance',
    tintBg: 'bg-primary/8',
    tintIcon: 'text-primary/35',
  },
  Comunidade: {
    badge: 'tertiary',
    icon: 'groups',
    tintBg: 'bg-tertiary/8',
    tintIcon: 'text-tertiary/35',
  },
};

const fallback = categoryConfig.Avisos;

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function NewsCard({ title, excerpt, category, date, slug, imageUrl }: NewsItem) {
  const config = (category && categoryConfig[category]) || fallback;

  return (
    <Link
      href={`/news/${slug}`}
      onClick={() => trackUmamiEvent('news_open', { slug, category, source: 'news_index' })}
      className="group surface-card hover:border-line-strong flex h-full flex-col overflow-hidden transition-all duration-200 hover:shadow-md"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={title}
          loading="lazy"
          decoding="async"
          className="h-44 w-full flex-shrink-0 object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div
          className={`flex h-44 w-full flex-shrink-0 items-center justify-center ${config.tintBg}`}
        >
          <Icon name={config.icon} size="xl" className={config.tintIcon} />
        </div>
      )}

      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex items-center gap-3">
          {category && <Badge label={category} color={config.badge} size="sm" />}
          {date && <span className="text-on-surface-light text-xs">{formatDate(date)}</span>}
        </div>

        <h3 className="font-headline text-on-surface group-hover:text-primary mb-2 text-base leading-snug font-semibold transition-colors">
          {title}
        </h3>

        <p className="text-on-surface-light line-clamp-3 flex-1 text-sm leading-relaxed">
          {excerpt}
        </p>

        <div className="border-line text-primary mt-3 flex items-center gap-1 border-t pt-3 text-sm font-semibold">
          Ler mais
          <Icon
            name="arrow_forward"
            size="sm"
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </div>
      </div>
    </Link>
  );
}
